import { useEffect, useRef } from 'react'

/*
 * Bulletproof live-data polling.
 *
 * Replaces the old fragile scheme (a setTimeout chain in App.js rescheduled off an
 * `eventListLoaded` redux change + a separate setInterval in TransactionMonitor) which could
 * silently die — a CSV export cleared the timer without rescheduling; a thrown poll never
 * rescheduled — and had no catch-up when a hidden tab's timers were throttled by the browser.
 *
 * The logic lives in `startLivePoll` (a plain function, no React) so it can be unit-tested
 * deterministically; `useLivePoll` is just the React lifecycle wrapper. Four guarantees:
 *
 *   1. NEVER STOPS ON ERROR — the next tick is scheduled in `finally`, so a failed poll still
 *      schedules the next. The loop is unkillable until you call stop().
 *   2. NEVER DOUBLE-POLLS — one self-rescheduling setTimeout (not setInterval) + an in-flight
 *      guard: at most one poll at a time, ticks never stack even if a request runs long.
 *   3. CATCHES UP INSTANTLY — polls immediately on visibilitychange→visible and window focus, so
 *      a returning admin never stares at a stale feed the browser froze while the tab was hidden.
 *   4. STOPS/RESTARTS CLEANLY — stop() clears the timer and unsubscribes; nothing leaks.
 */

// Framework-agnostic core. Starts polling immediately and returns a stop() function.
export function startLivePoll({ intervalMs, poll, onResult }) {
  let cancelled = false
  let timer = null
  let inFlight = false

  const schedule = () => {
    if (cancelled) return
    clearTimeout(timer)
    timer = setTimeout(run, intervalMs)
  }

  const run = async () => {
    // cancelled, or a poll is already running — don't stack; just (re)arm the timer.
    if (cancelled || inFlight) { schedule(); return }
    inFlight = true
    try {
      await poll()
      if (!cancelled && onResult) onResult(true)
    } catch (err) {
      if (!cancelled && onResult) onResult(false, err)
    } finally {
      inFlight = false
      schedule()          // ALWAYS reschedule — this is what makes the loop unkillable
    }
  }

  // instant catch-up when the tab becomes visible again (defeats background-tab timer throttling —
  // the #1 cause of a "frozen" feed after tabbing away). We listen to visibilitychange ONLY, not
  // window `focus`: a tab-return fires BOTH events, which would double-poll; and an unfocused-but-
  // still-visible window isn't throttled, so it needs no catch-up. visibilitychange covers the only
  // case that does (hidden -> visible).
  const catchUp = () => { if (!document.hidden) run() }

  run()   // poll immediately on start so activating the view shows fresh data at once
  document.addEventListener('visibilitychange', catchUp)

  return function stop() {
    cancelled = true
    clearTimeout(timer)
    document.removeEventListener('visibilitychange', catchUp)
  }
}

/*
 * React wrapper. Polls while `active` is true; pausing is just active=false (or unmount) — the
 * effect cleanup calls stop(). `poll`/`onResult` are read through refs so passing fresh closures
 * every render does NOT tear down and rebuild the loop; only `active`/`intervalMs` do.
 */
export function useLivePoll({ active, intervalMs, poll, onResult }) {
  const pollRef = useRef(poll)
  const onResultRef = useRef(onResult)
  pollRef.current = poll
  onResultRef.current = onResult

  useEffect(() => {
    if (!active) return
    const stop = startLivePoll({
      intervalMs,
      poll: () => pollRef.current(),
      onResult: (ok, err) => { if (onResultRef.current) onResultRef.current(ok, err) },
    })
    return stop
  }, [active, intervalMs])
}
